import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

// Brand Theme Colors - Premium Palette
const theme = {
  primaryDark: "#1b325f",
  primaryBlue: "#282f81",
  accentCyan: "#11abea",
  bgLight: "#f4f7fb",
  cardBg: "#ffffff",
  textMain: "#334155",
  textMuted: "#64748b",
  borderLight: "#e2e8f0",
  successSoft: "#dcfce7",
  successDark: "#166534",
  dangerSoft: "#fee2e2",
  dangerDark: "#991b1b"
};

const LabTestCategoryMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [categoriesList, setCategoriesList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const initialFormState = {
    test_category_id: "",
    test_category_name: "",
    status: "Active",
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- 1. SEARCH LOGIC ---
  const searchCategories = async (text) => {
    setSearchText(text);
    if (text.length < 1) {
      setCategoriesList([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/test-categories/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setCategoriesList(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectCategory = (cat) => {
    setFormData({ ...cat, _isExisting: true });
    setSearchText(`ID: ${cat.test_category_id} | ${cat.test_category_name}`);
    setShowDropdown(false);
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- 2. FORM ACTIONS (Auto-Increment Preview) ---
  const clearForm = async () => {
    try {
      // Auto-fetch the next ID for preview purposes
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/test-categories?employee_id=${user?.id}`);
      const allCategories = await res.json();
      const lastId = Math.max(...allCategories.map(c => parseInt(c.test_category_id) || 0), 0);
      
      setFormData({
        ...initialFormState,
        test_category_id: (lastId + 1).toString()
      });
    } catch (err) {
      setFormData({ ...initialFormState });
    }
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 3. SAVE LOGIC ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.test_category_name) {
      return alert("Test Category Name is required!");
    }

    const payload = { ...formData, created_by: user?.id };
    delete payload._isExisting;

    const method = formData._isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/test-categories" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/test-categories/${formData.test_category_id}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert("Lab Test Category Saved Successfully!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving Category (Check if name already exists)");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  // --- 4. PRINT FORM ---
  const printForm = () => {
    if (!formData.test_category_name) return alert("Please select a category to print.");
    
    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Test Category - ${formData.test_category_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #1b325f; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #11abea; font-size: 18px; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
            .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 160px; color: #555; }
            .value { flex: 1; color: #000; font-weight: 500; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Laboratory & Diagnostics Department</p>
            </div>
            <div class="report-title">
              <h2>TEST CATEGORY MASTER</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Category Profile</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Category ID:</span> <span class="value">${formData.test_category_id}</span></div>
            <div class="data-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
            <div class="data-item" style="grid-column: 1 / -1;"><span class="label">Category Name:</span> <span class="value" style="font-weight: bold; color: #282f81;">${formData.test_category_name}</span></div>
          </div>

          <div class="footer">
            System Generated Report - Emedha HMS
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      {/* SEARCH SECTION */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Lab Category</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by ID or Category Name (e.g., Cardiology)"
            onChange={(e) => searchCategories(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && categoriesList.length === 1) selectCategory(categoriesList[0]);
            }}
            autoFocus
          />
          {showDropdown && categoriesList.length > 0 && (
            <div className="patient-dropdown">
              {categoriesList.map((cat) => (
                <div key={cat.test_category_id} className="patient-item" onClick={() => selectCategory(cat)}>
                  ID: {cat.test_category_id} | {cat.test_category_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* LUXURY FORM */}
      <form className="luxury-form">
        <div className="form-section-header">
          <h3>Lab Test Category Information</h3>
        </div>
        
        <div className="form-grid">
          {/* ID Preview */}
          <div className="input-box read-only">
            <label>Category ID</label>
            <input type="text" value={formData.test_category_id} readOnly placeholder="Auto Generated" />
          </div>

          {/* Category Name */}
          <div className="input-box" style={{ gridColumn: 'span 2' }}>
            <label>Category Name <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              name="test_category_name" 
              value={formData.test_category_name} 
              onChange={handleChange} 
              disabled={isDisabled} 
              placeholder="e.g., Laboratory, Cardiology, Radiology" 
            />
          </div>

          {/* Status */}
          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={isDisabled}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LabTestCategoryMaster;